import React from 'react';
import { Upload, AlertTriangle } from 'lucide-react';
import type { UploadsSection } from '../../api/insights';
import shared from './shared.module.css';
import styles from './UploadHealthGroup.module.css';

interface Props {
  data: UploadsSection;
}

function TrendBars({ days }: { days: { date: string; count: number }[] }) {
  const max = Math.max(...days.map((d) => d.count), 1);
  return (
    <div className={shared['bar-chart']} aria-label="Upload trend last 14 days">
      {days.map((d) => {
        const pct = Math.round((d.count / max) * 100);
        const dayLabel = new Date(d.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'numeric', day: 'numeric' });
        return (
          <div key={d.date} className={shared['bar-col']} title={`${dayLabel}: ${d.count} uploads`}>
            <div className={shared['bar-fill']} style={{ height: `${Math.max(pct, 2)}%` }} />
            <div className={shared['bar-label']}>{dayLabel}</div>
          </div>
        );
      })}
    </div>
  );
}

function DonutSlices({ slices }: { slices: { label: string; value: number; color: string }[] }) {
  const total = slices.reduce((s, d) => s + d.value, 0);
  if (total === 0) return <span className={shared['sub-empty']}>No data yet</span>;
  let offset = 0;
  const r = 18;
  const circumference = 2 * Math.PI * r;
  return (
    <div className={styles['donut-wrap']}>
      <svg viewBox="0 0 44 44" className={styles['donut-svg']} aria-hidden="true">
        {slices.map((s) => {
          const dash = (s.value / total) * circumference;
          const el = (
            <circle
              key={s.label}
              cx="22" cy="22" r={r}
              fill="none"
              stroke={s.color}
              strokeWidth="8"
              strokeDasharray={`${dash} ${circumference - dash}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 22 22)"
            />
          );
          offset += dash;
          return el;
        })}
      </svg>
      <div className={styles['donut-legend']}>
        {slices.map((s) => (
          <div key={s.label} className={styles['legend-row']}>
            <span className={styles['legend-dot']} style={{ background: s.color }} />
            <span className={styles['legend-label']}>{s.label}</span>
            <span className={styles['legend-pct']}>{total > 0 ? Math.round((s.value / total) * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function UploadHealthGroup({ data }: Props) {
  const totalFailed = Object.values(data.failedByErrorCode).reduce((s, v) => s + v, 0);
  const hasFailures = totalFailed > 0;

  const sourcesTotal = data.bySource.ftp + data.bySource.manual;
  const formatTotal = data.byFormat.jpeg + data.byFormat.heic + data.byFormat.raw + data.byFormat.other;
  const maxPhotographerCount = data.topPhotographers[0]?.count ?? 1;

  return (
    <div className={shared['group-card']}>
      <div className={shared['group-header']}>
        <div className={shared['group-icon']}><Upload size={15} /></div>
        <div>
          <div className={shared['group-title']}>Upload Volume &amp; Health</div>
          <div className={shared['group-subtitle']}>Are photographers uploading successfully?</div>
        </div>
      </div>

      {/* Stat row */}
      <div className={shared['stat-row']}>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>All-time photos</span>
          <span className={shared['stat-value']}>{data.totalAllTime.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>This month</span>
          <span className={shared['stat-value']}>{data.totalThisMonth.toLocaleString()}</span>
        </div>
        <div className={shared['stat-card']}>
          <span className={shared['stat-label']}>Success rate</span>
          <span className={`${shared['stat-value']} ${data.successRate < 90 ? shared['stat-value--red'] : data.successRate < 98 ? shared['stat-value--amber'] : shared['stat-value--green']}`}>
            {data.successRate}%
          </span>
        </div>
      </div>

      {/* Trend chart */}
      <div>
        <div className={shared['section-label']}>Uploads — last 14 days</div>
        <TrendBars days={data.trendLast14Days} />
      </div>

      {/* Source & format donuts */}
      <div className={styles['donuts-row']}>
        <div className={styles['donut-section']}>
          <div className={shared['section-label']}>Upload source</div>
          <DonutSlices slices={[
            { label: 'FTP', value: data.bySource.ftp, color: 'var(--clay-orange)' },
            { label: 'Manual', value: data.bySource.manual, color: 'rgba(239,68,68,0.35)' },
          ]} />
        </div>
        <div className={styles['donut-section']}>
          <div className={shared['section-label']}>File format</div>
          <DonutSlices slices={[
            { label: 'JPEG', value: data.byFormat.jpeg, color: 'var(--clay-orange)' },
            { label: 'HEIC', value: data.byFormat.heic, color: '#f59e0b' },
            { label: 'RAW', value: data.byFormat.raw, color: '#22c55e' },
            { label: 'Other', value: data.byFormat.other, color: 'var(--border-subtle)' },
          ]} />
        </div>
      </div>

      {/* Failed uploads */}
      {hasFailures && (
        <div className={styles['failures-section']}>
          <div className={shared['section-label']}>
            <AlertTriangle size={11} style={{ verticalAlign: 'middle', marginRight: 3 }} />
            Failed uploads ({totalFailed})
          </div>
          <div className={styles['failure-list']}>
            {Object.entries(data.failedByErrorCode).map(([code, count]) => (
              <div key={code} className={styles['failure-row']}>
                <span className={styles['failure-code']}>{code.replace(/_/g, ' ')}</span>
                <span className={`${shared.badge} ${shared['badge--red']}`}>{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top photographers */}
      {data.topPhotographers.length > 0 && (
        <div>
          <div className={shared['section-label']}>Top photographers</div>
          <div className={shared['ranked-list']}>
            {data.topPhotographers.slice(0, 5).map((p, i) => (
              <div key={p.ftpAccountId} className={shared['ranked-item']}>
                <div className={shared['rank-badge']}>{i + 1}</div>
                <span className={shared['ranked-label']}>{p.photographerName || 'Unnamed'}</span>
                <div
                  className={shared['ranked-bar']}
                  style={{ width: `${Math.round((p.count / maxPhotographerCount) * 80)}px` }}
                />
                <span className={shared['ranked-count']}>{p.count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
